import { TextInput, TextInputProps } from "@mantine/core";
import { useRef } from "react";

interface PhoneNumberInputProps extends Omit<TextInputProps, "onChange"> {
  onChange: (value: string) => void;
}

export function PhoneNumberInput({ onChange, value, ...props }: PhoneNumberInputProps) {
  const previousValueRef = useRef<string>("");

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) {
      return digits;
    }
    if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
  };

  const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.currentTarget.value;
    const numericValue = rawValue.replace(/\D/g, "");
    const previousNumericValue = previousValueRef.current;

    // If the numeric value hasn't changed but the raw value is shorter,
    // it means the user deleted a dash - remove the last digit
    if (
      numericValue === previousNumericValue &&
      rawValue.length < formatPhoneNumber(previousNumericValue).length
    ) {
      const newValue = numericValue.slice(0, -1);
      previousValueRef.current = newValue;
      onChange(newValue);
      return;
    }

    const truncatedValue = numericValue.slice(0, 11);
    previousValueRef.current = truncatedValue;
    onChange(truncatedValue);
  };

  return (
    <TextInput
      {...props}
      value={formatPhoneNumber(value?.toString() ?? "")}
      onChange={handleNumericChange}
      maxLength={13}
      type="tel"
      inputMode="numeric"
      placeholder="XXX-XXXX-XXXX"
    />
  );
}
